import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Tooltip,
} from '@mui/material';
import { fonts, spacing, borderRadius, colors } from '../../constants';

/**
 * SidebarMenuSection Component
 * Renders menu sections with items and handles navigation
 * 
 * @param {Array} menuSections - Array of menu sections with items
 * @param {boolean} isCollapsed - Whether the sidebar is collapsed
 * @param {function} isActiveRoute - Function to check if a route is active
 * @param {function} onNavigate - Callback function for navigation
 */
const SidebarMenuSection = ({ menuSections, isCollapsed, isActiveRoute, onNavigate }) => {
  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        px: isCollapsed ? 0 : spacing.sm,
        py: 1,
        // Hide scrollbar completely
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE and Edge
      }}
    >
      {menuSections.map((section, sectionIndex) => (
        <Box
          key={section.title}
          sx={{
            mb: sectionIndex < menuSections.length - 1 ? 1.5 : 0,
          }}
        >
          {!isCollapsed && section.title !== 'Main' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: spacing.sm,
                mb: 2,
                mt: sectionIndex > 0 ? 2.25 : 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontWeight: fonts.weightSemiBold,
                  fontSize: '11px',
                  letterSpacing: '0.05em',
                  lineHeight: 1.2,
                  flexShrink: 0,
                  mr: spacing.xs,
                  textTransform: 'uppercase',
                }}
              >
                {section.title}
              </Typography>
              <Divider
                sx={{
                  flex: 1,
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  borderWidth: '1px',
                }}
              />
            </Box>
          )}
          <List sx={{ p: 0, mt: !isCollapsed && section.title !== 'Main' ? 0.75 : 0 }}>
            {section.items.map((item) => {
              const isActive = isActiveRoute(item.path);
              return (
                <ListItem key={item.name} disablePadding sx={{ mb: 0.5, px: isCollapsed && isActive ? spacing.xs : 0 }}>
                  <Tooltip title={isCollapsed ? item.name : ''} placement="right" arrow>
                    <ListItemButton
                      selected={isActive}
                      onClick={() => onNavigate(item.path)}
                      sx={{
                        borderRadius: borderRadius.sm,
                        minHeight: 36,
                        height: 36,
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                        px: isCollapsed ? spacing.xs : spacing.sm,
                        py: 0,
                        mb: 0,
                        '&.Mui-selected': {
                          background: colors.sidebarActiveBackground,
                          backdropFilter: 'blur(12px)',
                          WebkitBackdropFilter: 'blur(12px)',
                          border: 'none',
                          boxShadow: `0 4px 15px ${colors.sidebarShadow}`,
                          color: colors.sidebarActiveText,
                          '& .MuiListItemText-primary': {
                            color: colors.sidebarActiveText,
                          },
                          '&:hover': {
                            background: colors.sidebarActiveBackground,
                            boxShadow: `0 6px 18px ${colors.sidebarShadowAccent}`,
                            color: colors.sidebarActiveText,
                          },
                          '& .MuiListItemIcon-root': {
                            color: colors.sidebarActiveText,
                          },
                        },
                        '&:hover': {
                          backgroundColor: isActive ? 'transparent' : 'rgba(255, 255, 255, 0.05)',
                          color: isActive ? colors.sidebarActiveText : 'rgba(255, 255, 255, 0.9)',
                          '& .MuiListItemIcon-root': {
                            color: isActive ? colors.sidebarActiveText : 'rgba(255, 255, 255, 0.9)',
                          },
                          '& .MuiListItemText-primary': {
                            color: isActive ? colors.sidebarActiveText : 'rgba(255, 255, 255, 0.9)',
                          },
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: isCollapsed ? 0 : 28,
                          color: isActive
                            ? '#1e293b'
                            : 'rgba(255, 255, 255, 0.7)',
                          justifyContent: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          '& svg': {
                            fontSize: '20px',
                            width: '20px',
                            height: '20px',
                            strokeWidth: isActive ? 2 : 1.5,
                            transition: 'all 0.2s ease',
                          },
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {!isCollapsed && (
                        <ListItemText
                          primary={item.name}
                          primaryTypographyProps={{
                            fontSize: '13.5px',
                            fontWeight: isActive ? fonts.weightBold : fonts.weightSemiBold,
                        color: isActive ? colors.sidebarActiveText : 'rgba(255, 255, 255, 0.85)',
                            letterSpacing: '-0.01em',
                            lineHeight: 1.3,
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        </Box>
      ))}
    </Box>
  );
};

export default SidebarMenuSection;
